import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminData, logoutAdmin } from '../../utils/adminAuth';

interface TopbarProps {
  breadcrumb: {
    section: string;
    page: string;
  };
  title: string;
  subtitle: string;
  onToggleSidebar: () => void;
}

export default function Topbar({ breadcrumb, title, subtitle, onToggleSidebar }: TopbarProps) {
  const [adminName, setAdminName] = useState('Admin');
  const [initials, setInitials] = useState('AD');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const adminData = getAdminData();
    if (adminData) {
      setAdminName(adminData.name);
      // Generate initials from name (first letter of first and last name)
      const nameParts = adminData.name.split(' ');
      const firstInitial = nameParts[0]?.[0] || '';
      const lastInitial = nameParts[nameParts.length - 1]?.[0] || '';
      setInitials((firstInitial + lastInitial).toUpperCase());
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };
  return (
    <header className="h-14 sm:h-16 flex items-center justify-between px-3 sm:px-6 border-b border-border-soft backdrop-blur-[14px] bg-gradient-to-r from-[rgba(7,8,14,0.9)] to-[rgba(7,8,14,0.75)] sticky top-0 z-10 gap-2">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        {/* Hamburger menu - visible only on mobile */}
        <button
          onClick={onToggleSidebar}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl border border-border-soft bg-white/[0.02] hover:bg-white/5 transition-colors flex-shrink-0"
          aria-label="Menu"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[0.72rem] text-text-soft">
            <span className="hidden sm:inline">{breadcrumb.section}</span>
            <span className="hidden sm:inline">›</span>
            <span className="truncate">{breadcrumb.page}</span>
          </div>
          <div className="text-[0.9rem] sm:text-[1.05rem] font-medium truncate">{title}</div>
          <div className="text-[0.72rem] sm:text-[0.78rem] text-text-soft hidden sm:block">{subtitle}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        {/* Search - hidden on mobile */}
        <div className="relative min-w-[260px] hidden md:block">
          <input
            type="search"
            placeholder="Procurar cliente, sessão ou consultor…"
            className="w-full bg-[radial-gradient(circle_at_top,#1a1e30_0,#111421_70%)] rounded-full border border-border-soft px-3.5 pr-10 py-2 text-text text-[0.82rem] outline-none placeholder:text-[#6c7390]"
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[0.82rem] opacity-80">⌕</span>
        </div>

        <button className="w-8 h-8 rounded-full border border-border-soft flex items-center justify-center text-[0.85rem] cursor-pointer bg-white/[0.02] hover:bg-white/5 transition-colors relative">
          ⌁
          <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-accent" />
        </button>

        <div className="relative" ref={dropdownRef}>
          <div 
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2 px-2 py-1 rounded-full border border-border-soft cursor-pointer text-[0.78rem] bg-[rgba(12,14,25,0.8)] hover:bg-white/5 transition-colors"
          >
            <div className="w-[26px] h-[26px] rounded-full bg-gradient-to-br from-accent to-[#e86fde] flex items-center justify-center text-[0.78rem] font-semibold">
              {initials}
            </div>
            <div className="flex-col leading-tight hidden sm:flex">
              <span className="text-[0.78rem]">{adminName}</span>
              <span className="text-[0.7rem] text-text-soft">Admin</span>
            </div>
          </div>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-[rgba(21,24,35,0.98)] border border-border-soft rounded-lg shadow-lg overflow-hidden backdrop-blur-xl z-50">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2.5 text-left text-[0.82rem] text-text hover:bg-white/5 transition-colors flex items-center gap-2"
              >
                <span>⎋</span>
                <span>Sair</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
